import React from "react";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, RequireAuth, useAuth } from "../providers/AuthProvider";
import { QueryProvider } from "../providers/QueryProvider";
import { ToastContainer } from "@kkp/ui";

import Login from "../../pages/Login";
import DashboardLayout from "../../pages/Dashboard/Layout";
import ClientsPage from "../../pages/Clients";
import CompanyDetailPage from "../../pages/Company/Detail";
import FdsQueuePage from "../../pages/Fds";
import FdsCaseDetailPage from "../../pages/Fds/CaseDetail";
import LogsPage from "../../pages/Logs";

const SandboxPage = __SANDBOX_BUILD__
  ? React.lazy(() => import("../../pages/Sandbox"))
  : null;

// MANUAL_BUILD-only (dev/local): the Demo App gallery, addressable per section for
// the P6 manual-gen pipeline (scripts/manual-gen.mjs). Gated on __MANUAL_GALLERY__
// — NOT __SANDBOX_BUILD__ — so it is excluded from BOTH deployed admin bundles (prod
// AND the Sandbox-Control sandbox); it exists only under `just manual-gen`/`dev-manual`.
// Auth-gated + inside the dashboard shell, and intentionally NOT linked from the
// sidebar — an agent reaches it by URL, so human nav stays unchanged.
const GalleryRoute = __MANUAL_GALLERY__
  ? React.lazy(() => import("../../pages/Sandbox/GalleryRoute"))
  : null;

/**
 * AppRouter
 *
 * CSR-only routing using HashRouter (per BLUEPRINT.md) to avoid server rewrites.
 *
 * Routes:
 * - /#/login
 * - /#/ -> redirect to /#/company if authed else /#/login
 * - Protected:
 *   - /#/company, /#/company/:companyId
 *   - /#/fds, /#/fds/:caseId
 *   - /#/logs
 *   - /#/sandbox (SANDBOX_BUILD only — Sandbox Control; deployed to the sandbox tier)
 *   - /#/gallery, /#/gallery/:section (MANUAL_BUILD only — dev/local Demo App gallery
 *     for the P6 manual-gen pipeline; agent-reached by URL, never in a deploy)
 */
export function AppRouter() {
  return (
    <QueryProvider>
      <AuthProvider>
        {/* Toast UI (Flowbite-style, brand-themed) */}
        <ToastContainer position="top-right" />

        <HashRouter>
          <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route element={<RequireAuth />}>
              <Route element={<DashboardLayout />}>
                <Route path="/company" element={<ClientsPage />} />
                <Route
                  path="/company/:companyId"
                  element={<CompanyDetailPage />}
                />
                <Route path="/fds" element={<FdsQueuePage />} />
                <Route path="/fds/:caseId" element={<FdsCaseDetailPage />} />
                <Route path="/logs" element={<LogsPage />} />
                {SandboxPage && (
                  <Route
                    path="/sandbox"
                    element={
                      <React.Suspense fallback={null}>
                        <SandboxPage />
                      </React.Suspense>
                    }
                  />
                )}
                {GalleryRoute &&
                  ["/gallery", "/gallery/:section"].map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <React.Suspense fallback={null}>
                          <GalleryRoute />
                        </React.Suspense>
                      }
                    />
                  ))}
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </AuthProvider>
    </QueryProvider>
  );
}

function IndexRedirect() {
  const auth = useAuth();
  return <Navigate to={auth.isAuthenticated ? "/company" : "/login"} replace />;
}
